/*
 * Copyright (c) Nexign, JSC, 2019.
 */
/**
 *
 * @author Vlaidslav Oshkanov
 * @author Yakov Gushchin
 * @version 1.0.1
 * @since 1.0.0
 */
import { Component } from 'react';

import { DragDropContext, Droppable, DroppableProvided, DropResult } from 'react-beautiful-dnd';

import { icons } from '../../../../assets';
import DroppableContainer from './DroppableContainer';
import { DragListContainerStyled } from './styled/DragAndDropStyled';

/**
 * @description Interface for drag and drop container.
 * @interface IDragAndDropProps
 *
 * @property {string} icon Define icon for move items.
 * @property {IPersonalAccountField[]} items Define items for render in container.
 * @property {(items: IPersonalAccountField[]) => void} onChange handler when items positions.
 */
export interface IDragAndDropProps {
    icon: string;
    items: JSX.Element[];
    onChange: (items: JSX.Element[]) => void;
}

/**
 * @description Component which renders list of custom react components and allows them to be reordered.
 * @class DragAndDrop
 * @extends Component<IDragAndDropProps>
 */
class DragAndDrop extends Component<IDragAndDropProps> {

    static defaultProps = {
        icon: icons.dragIcon,
    };

    /**
     * @description function to help with reordering the result
     * @param {JSX.Element[]} list
     * @param {number} startIndex
     * @param {number} endIndex
     * @return {JSX.Element[]}
     */
    reorder = (list: JSX.Element[], startIndex: number, endIndex: number) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    /**
     * @description Method called by react-beautiful-dnd which passes result of drag and drop event to it.
     * @summary Function changes element's historyOrder in state and invokes callback onChange.
     * @param result
     * @return {VoidFunction}
     */
    onDragEnd = (result: DropResult) => {
        const { destination, source } = result;
        // if item dropped outside list
        if (!destination) {
            return;
        }
        // if item position doesn't change
        if (destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        this.props.onChange(this.reorder(this.props.items, source.index, destination.index));
    };

    render() {
        return (
            <DragListContainerStyled>
                <DragDropContext onDragEnd={(result: DropResult) => this.onDragEnd(result)}>
                    <Droppable droppableId={'1'}>
                        {
                            (provided: DroppableProvided) => (
                                <DroppableContainer
                                    icon={this.props.icon}
                                    items={this.props.items}
                                    provided={provided}
                                />
                            )
                        }
                    </Droppable>
                </DragDropContext>
            </DragListContainerStyled>
        );
    }
}

export default DragAndDrop;
